import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { forkJoin, Observable, of, timer } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AlertService } from './alert.service';
import { User } from '../models/user';
import { AppService } from './app.service';
import { CustomerService } from './customer.service';
import { DateTimeService } from './date-time.service';
import { ErrorService } from './error.service';
import { GlobalService } from './global.service';
import { OrderService } from './order.service';
import { PushService } from './push.service';
import { UserService } from './user.service';
import { WorkerService } from './worker.service';
import { ModalService } from './modal.service';
import { AppConfig } from '../app.config';
import { MfaChallengeService } from './mfa-challenge.service';
import { SpinnerService } from './spinner.service';

@Injectable()
export class AuthenticationService {
	private static readonly USER_KEY = 'Rapidship:user';
	private static readonly CUSTOMER_KEY = 'Rapidship:customer';
	private static readonly SESSION_EXPIRATION_KEY = 'Rapidship:sessionExpiration';

	public idleState$: EventEmitter<any>;
	public loggedIn$: EventEmitter<any>;
	private sessionExpiration: Date | null = null;
	public showAuthModal$: EventEmitter<any>;
	public showTimeoutBanner$: EventEmitter<any>;
	public showWhatsNew$: EventEmitter<boolean>;
	public timerSubscription: any;
	private bannerCurrentlyShowing: boolean = false;
	public user = new User();
	public version: any;
  	public whatsNewVersion: string = '22.4.0.0';
	public sessionTimeoutMinutes: number = 20;

	constructor(private _http: HttpClient,
				private _userService: UserService,
				private _dateTimeService: DateTimeService,
				private _customerService: CustomerService,
				private _workerService: WorkerService,
				private _globalService: GlobalService,
				private _errorService: ErrorService,
				private _appService: AppService,
				private _orderService: OrderService,
				private _pushService: PushService,
				private _alertService: AlertService,
				private _modalService: ModalService,
				private _appConfig: AppConfig,
				private _mfaChallengeService: MfaChallengeService,
				private _spinnerService: SpinnerService) {
		this.showTimeoutBanner$ = new EventEmitter();
		this.idleState$ = new EventEmitter<any>();
		this.loggedIn$ = new EventEmitter<any>();
		this.showAuthModal$ = new EventEmitter<boolean>();
		this.showWhatsNew$ = new EventEmitter<boolean>();
		this.version = this._appService.version;
		this.setupUserInteractionListeners();
	}

	set expirationOfSession(val: Date | null) {
		this.sessionExpiration = val;
		if (val) {
			localStorage.setItem(AuthenticationService.SESSION_EXPIRATION_KEY, val.toISOString());
		} else {
			localStorage.removeItem(AuthenticationService.SESSION_EXPIRATION_KEY);
		}
	}

	private setupUserInteractionListeners() {
		// Listen for user interactions to reset session and hide banner
		const events = ['click', 'keydown', 'input', 'change', 'focus'];

		events.forEach(eventType => {
			document.addEventListener(eventType, () => {
				this.onUserInteraction();
			}, true);
		});
	}

	private onUserInteraction() {
		// Reset session timeout when user interacts with page
		if (this.sessionExpiration && this.bannerCurrentlyShowing) {
			const sessionTimeoutMinutes = this.sessionTimeoutMinutes || 20;
			const newSessionExpiration = new Date();
			newSessionExpiration.setMinutes(newSessionExpiration.getMinutes() + sessionTimeoutMinutes);
			this.expirationOfSession = newSessionExpiration;
		}
	}

	authenticationDelete(reason?) {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
		}

		// Clear any in-flight spinner as part of teardown. The spinner is itself a MatDialog opened inside a
		// setTimeout, while ModalService.displayLoginModal() begins with dialog.closeAll() — so a spinner
		// started just before a logout can open AFTER the close-all and sit permanently over the login page,
		// because SpinnerService was never told to stop. stopSpinner() is a no-op when nothing is running and
		// retries itself if the deferred open has not happened yet.
		this._spinnerService.stopSpinner();

		if (reason) {
			this.showAuthModal(reason);
		}
		this._userService.deleteUser();
		localStorage.removeItem(AuthenticationService.CUSTOMER_KEY);
		// Browser-remember tokens must survive logout — that is the whole point of the
		// "Every N days" challenge frequency (skip MFA on a trusted device across sessions).
		// They are keyed per-username, and expiry / bulk-revoke are enforced server-side, so
		// a stale local token is rejected by ValidateToken rather than needing a client wipe.
		this._mfaChallengeService.clearCompletedCustomers();
		this.expirationOfSession = null;
		this._appConfig.getAnonymousOptions();
		this.showTimeoutBanner$.emit(false);
	}

	/**
	 * @api {post} /v2/Authentication/CaptchaVerification Captcha Verification
	 * @apiName captchaVerification
	 * @apiGroup Authentication
	 * @apiVersion 20.0.0
	 */
	captchaVerification(token) {
		return this._http.post<any>(`${environment.apiUrl}/v2/Authentication/CaptchaVerification`, JSON.stringify(token));
	}

	/**
	 * @api {post} /v2/Authentication/EmailExists Check If Email Already Exists
	 * @apiName emailExists
	 * @apiGroup Authorization
	 * @apiVersion 20.0.0
	 */
	emailExists(data) {
		return this._http.post<any>(`${environment.apiUrl}/v2/Authorization/EmailExists`, data);
	}

	initiateLogin(credentials, userType) {
		return new Observable((observer) => {
			this.login(credentials, userType).subscribe({
				next: (response: any) => {
					this.loginInitiationSuccess(credentials, userType, observer, response);
				}, error: (error) => {
					observer.error(error);
				}
			});
		});
	}

	loginInitiationSuccess(credentials, userType, observer, response, isSsoSession = false) {
		this._orderService.clearOrderQueue();
		// H4 (amonea): the customer step-up memo is a service singleton that outlives a user change in the same
		// tab (several teardown paths call deleteUser() without authenticationDelete, which is the only clearer).
		// Clear it at the start of every login so a new user cannot inherit the previous session's "already
		// stepped up" markers and skip the step-up challenge.
		this._mfaChallengeService.clearCompletedCustomers();
		this.user = new User();
		this.user.type = userType;
		this.user.username = credentials.username;
		this.user.token = response.token ? response.token : response;
		// Defaults to false, and `new User()` above discards any previous session's value — so a password
		// login after an SSO one in the same browser cannot inherit the SSO gating and lose its controls.
		this.user.isSsoSession = isSsoSession;
		this._userService.updateUser(this.user);
		// if rememberMe is checked save username to localStorage plus the userType else if unchecked and is set then remove it.
		if (credentials.rememberMe) {
			localStorage.setItem('Rapidship:rememberMe', JSON.stringify({
				username: credentials.username,
				userType: this.user.type
			}));
		} else if (localStorage.getItem('Rapidship:rememberMe')) {
			localStorage.removeItem('Rapidship:rememberMe');
		}

		if (this.user.type === 'inet') {
			if (response.profile) {
				Object.assign(this.user, this.user, response.profile);
				this.user.mfaChallengePending = false;
				this.user.mfaChallengeSessionId = null;
				this.user.mfaPreferredMethod = null;
				this.user.mfaAvailableMethods = null;
				this.user.mfaMaskedEmail = null;
				this.user.mfaNotifyPending = false;
				this.user.mfaDisabledMethodNames = null;
				this.user.mfaEffectivePolicy = null;
				this.user.mfaGracePeriodActive = null;
				this.user.mfaGracePeriodDaysRemaining = null;
				this.user.mfaAllowedMethodsForEnrollment = null;
				// Enrollment is driven by the backend's authoritative mfaAction (same source as challenge/notify),
				// not re-derived from flags. Re-deriving it wrongly suppressed the wizard under Enforced when a
				// stale Optional-era wizard-skip flag was set, leaving a PreAuth token that locked the user out.
				this.user.mfaEnrollmentPending = response.mfaAction === 'enroll';
				if (this.user.mfaEnrollmentPending) {
					// The enrollment wizard shows these when it is non-skippable (expired grace / forced
					// enrollment), which is the one state where the user cannot help themselves.
					this.user.mfaContactAdminPhone = response.mfaContactAdminPhone;
					this.user.mfaContactAdminEmail = response.mfaContactAdminEmail;
				}
				if (response.mfaChallengePending) {
					this.user.mfaChallengePending = response.mfaChallengePending;
					this.user.mfaChallengeSessionId = response.mfaChallengeSessionId;
					this.user.mfaPreferredMethod = response.mfaPreferredMethod;
					this.user.mfaAvailableMethods = response.mfaAvailableMethods;
					this.user.mfaMaskedEmail = response.mfaMaskedEmail;
					this.user.mfaOtpTtlMinutes = response.mfaOtpTtlMinutes;
					this.user.mfaOtpResendDelaySeconds = response.mfaOtpResendDelaySeconds;
					this.user.mfaMaxAttempts = response.mfaMaxAttempts;
					this.user.mfaContactAdminPhone = response.mfaContactAdminPhone;
					this.user.mfaContactAdminEmail = response.mfaContactAdminEmail;
				}
				if (response.mfaNotifyPending) {
					this.user.mfaNotifyPending = response.mfaNotifyPending;
					this.user.mfaDisabledMethodNames = response.mfaDisabledMethodNames;
					this.user.mfaEffectivePolicy = response.mfaEffectivePolicy;
					this.user.mfaGracePeriodActive = response.mfaGracePeriodActive;
					this.user.mfaGracePeriodDaysRemaining = response.mfaGracePeriodDaysRemaining;
					this.user.mfaAllowedMethodsForEnrollment = response.mfaAllowedMethodsForEnrollment;
					this.user.mfaContactAdminPhone = response.mfaContactAdminPhone;
					this.user.mfaContactAdminEmail = response.mfaContactAdminEmail;
				}
				this._userService.updateUser(this.user);
				observer.next('proceed'); observer.complete(); // defer app-data load until MFA resolved; a PreAuth token 401s data endpoints and would force a spurious logout
			} else {
				observer.error({message: 'Did not retrieve necessary profile data, unable to log you in!'});
			}
		} else {
			this.loginWorker(this.user, observer);
		}
	}

	login(loginCreds, type) {
		return new Observable((observer) => {
			if (type === 'inet') {
				this.loginInetUser(loginCreds).subscribe({
					next: (response) => {
						observer.next(response);
						observer.complete();
					}, error: (error) => {
						observer.error(error);
					}
				});
			} else {
				this.loginHRUser(loginCreds).subscribe({
					next: (response) => {
						observer.next(response);
						observer.complete();
					}, error: (error) => {
						observer.error(error);
					}
				});
			}
		});
	}

	/**
	 * @api {post} /v2/Authentication/Worker Login HR User
	 * @apiName loginHRUser
	 * @apiGroup Authentication
	 * @apiVersion 20.0.0
	 */
	loginHRUser(loginCreds) {
		const workerCreds = {
			username: loginCreds.username,
			password: loginCreds.password
		};
		return this._http.post<any>(`${environment.apiUrl}/v2/Authentication/Worker`, JSON.stringify(workerCreds));
	}

	// Runs the post-authentication data cascade (global options, allowed customers, selected
	// customer, licenses, misc data) and returns an Observable. Called only after MFA is resolved
	// so the token is Active; a PreAuth token would 401 on these endpoints.
	loadInetUserData(user): Observable<any> {
		return new Observable((observer) => {
			this.loginInet(user, observer);
		});
	}

	loginInet(user, observer) {
		this._globalService.getGlobalOptions().subscribe({
			next: (response) => {
				user.globalSettings = {...user.globalSettings, ...response};
				this._dateTimeService.processDateTimeFormat(response.dateFormat, response.timeDisplayFormat);
				const obs = [this._customerService.getAllowedCustomers()];
				if (user.inetUser.canViewShippingOptions) {
					obs.push(this._orderService.getAvailableShippingLabels());
				}
				forkJoin(obs).subscribe({
					next: ([allowedCustomers, shippingLabels]) => {
						// There is a permission that can be true to use shipping labels but there can be
						// no shipping labels to use so set permission to false if no labels so options are appropriately hidden
						// same applies to printShippingLabel which will auto print shipping labels even if no labels exist :/
						if (shippingLabels?.length === 0) {
							user.inetUser.canViewShippingOptions = false;
							user.inetUser.printShippingLabel = false;
						}
						Object.assign(user, user, {allowedCustomers: allowedCustomers});
						const selectedCustomer = user.allowedCustomers.filter((customer) => customer.id === user.primaryCustomerId)[0];
						forkJoin([
							this._customerService.getCustomer(selectedCustomer.id),
							this._globalService.getLicenses(),
							this._userService.getInetUserMiscData()
						])
							.subscribe({
								next: ([customer, licenses, miscData]) => {
									this._customerService.updateSelectedCustomer(customer);
									Object.assign(user, user, {licenses: licenses});
									user.inetUser.miscData = miscData;
									// Check if inet user has saved misc and if the applicationVersion matches current version and if current matches whatsNewVersion
									if (user.inetUser.miscData === null || (user.inetUser.miscData && user.inetUser.miscData.applicationVersion !== this.version && user.inetUser.miscData.applicationVersion < this.whatsNewVersion)) {
										this.showWhatsNew$.emit(true);
										if (!user.inetUser.miscData) {
											user.inetUser.miscData = {};
										}
										user.inetUser.miscData.applicationVersion = this.version;
										this._userService.setInetUserMiscData(user.inetUser.miscData).subscribe({
											next: () => {

											}, error: (error) => {
												this._errorService.error(error, 'set setInetUserMiscData failed');
											}
										});
									}
									this._userService.updateUser(user);
									this._pushService.registerForPush();
									observer.next('place-order');
									observer.complete();
								}, error: (error) => {
									console.warn(error);
									observer.error(error);
								}
							});
					}, error: (error) => {
						console.warn(error);
						observer.error(error);
					}
				});
			}, error: (error) => {
				observer.error(error);
			}
		});

		this.loggedIn$.emit(user.type);
	}

	/**
	 * @api {post} /v2/Authentication/InetUser?retrieveProfile=true Login Inet User
	 * @apiName loginInetUser
	 * @apiParam {boolean} [retrieveProfile] Retrieve Profile
	 * @apiGroup Authentication
	 * @apiVersion 20.0.0
	 */
	loginInetUser(loginCreds) {
		let params: HttpParams = new HttpParams();
		params = params.append('retrieveProfile', 'true');
		const payload = { ...loginCreds };
		// F-161: read through the service that owns the key format (and its lowercasing) rather than
		// hand-building the string here — a key-shape change otherwise silently stops "remember this browser".
		const mfaToken = this._mfaChallengeService.getMfaToken(loginCreds.username);
		if (mfaToken) {
			payload.mfaSessionToken = mfaToken;
		}
		return this._http.post<any>(`${environment.apiUrl}/v2/Authentication/InetUser`, JSON.stringify(payload), {params});
	}

	loginWorker(user, observer) {
		this._workerService.getHumanResourceProfile().subscribe({
			next: (response) => {
				Object.assign(user, user, response);
				user.username = parseInt(user.username, 10);
				this._userService.updateUser(user);
				this.loggedIn$.emit(user.type);
				this._workerService.getAssociatedDrivers().subscribe({
					next: (associatedDrivers) => {
						if (associatedDrivers && associatedDrivers.length > 0) {
							const matchingAssociatedDriver = associatedDrivers.find((ad) => ad.id === user.username);
							user.driverId = matchingAssociatedDriver ? matchingAssociatedDriver.id : associatedDrivers[0].id;
							Object.assign(user, {associatedDrivers: associatedDrivers});
							this._userService.updateUser(user);
						}
						observer.next('work');
						observer.complete();
					}, error: (error) => {
						observer.error(error);
					}
				});
			}, error: (error) => {
				observer.error(error);
			}
		});
	}

	/**
	 * @api {delete} /v2/Authentication Logout
	 * @apiName logout
	 * @apiGroup Authentication
	 * @apiVersion 20.0.0
	 */
	logout() {
		this._alertService.clear();
		localStorage.removeItem(AuthenticationService.CUSTOMER_KEY);
		this.expirationOfSession = null;
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
		}
		const currentUser = this._userService.getUser();
		if (!currentUser || !currentUser.token) {
			// Skip the server-side logout when no token is held (e.g. a stale ;logout route param
			// re-processed on refresh). Calling DELETE without a token returns 401 and adds a
			// spurious auth failure to the log sinks. Local cleanup still runs via the caller.
			return of(null);
		}
		return this._http.delete<any>(`${environment.apiUrl}/v2/Authentication`);
	}

	/**
	 * @api {post} /v2/Authentication/IsAlive Check If Token Is Valid
	 * @apiName renewSession
	 * @apiGroup Authentication
	 * @apiVersion 20.0.0
	 */
	renewSession(user) {
		return this._http.post<any>(`${environment.apiUrl}/v2/Authentication/IsAlive`, {userId: user.username, token: user.token});
	}

	/**
	 * XD-62456: whether guest ordering is still switched on. This is the same predicate the backend re-checks on
	 * every guest request (GuestInetUserIsAllowed: Mercury processing, guest row approved, primary customer set),
	 * exposed anonymously as allowGuest. Only an explicit false counts as off.
	 */
	isGuestOrderingAllowed(): Observable<boolean> {
		return this._http.get<any>(`${environment.apiUrl}/v2/Global/Profile/AnonymousOptions`)
			.pipe(map((options) => options?.allowGuest !== false));
	}

	showAuthModal(error?) {
		this.showAuthModal$.emit(error);
	}

	/**
	 * A complete, self-contained sign-out: server-side session DELETE, then local cleanup and the auth modal.
	 * Mirrors what the `/home;logout` route does, for callers that cannot route there — notably the MFA
	 * enrollment wizard, which is opened from a router guard, so navigating from inside it would race a
	 * navigation that has not resolved yet and the wizard would simply re-open.
	 * `authenticationDelete()` on its own is NOT equivalent: it clears local state and leaves the token live.
	 */
	performLogout(): void {
		this.logout()
			.pipe(finalize(() => {
				this.authenticationDelete();
				this.showAuthModal();
			}))
			.subscribe({
				error: (err) => console.warn('Logout DELETE failed; local cleanup proceeded via finalize', err)
			});
	}

	startSessionTimer() {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
		}

		if (!this.sessionExpiration) {
			const stored = localStorage.getItem(AuthenticationService.SESSION_EXPIRATION_KEY);
			if (stored) {
				const restored = new Date(stored);
				if (isNaN(restored.getTime()) || restored.getTime() <= Date.now()) {
					localStorage.removeItem(AuthenticationService.USER_KEY);
					localStorage.removeItem(AuthenticationService.CUSTOMER_KEY);
					localStorage.removeItem(AuthenticationService.SESSION_EXPIRATION_KEY);
					// Hard redirect instead of authenticationDelete() to avoid race conditions
					// with live components making API calls during teardown
					window.location.href = '/';
					return;
				}
				this.expirationOfSession = restored;
			}
		}

		this.timerSubscription = timer(0, 1000).subscribe(() => {
			if (this.sessionExpiration) {
				const now = new Date();
				const diff = this.sessionExpiration.getTime() - now.getTime();
				const timeoutTimer = diff / 1000;
				const minutes = Math.floor(timeoutTimer / 60);
				const seconds = Math.floor(timeoutTimer - minutes * 60);
				this.idleState$.emit('Your session will timeout in: ' + minutes + ' minutes ' + seconds + ' seconds');

				const warningThreshold = 300;

				if (timeoutTimer <= warningThreshold) {
					if (!this.bannerCurrentlyShowing) {
						this.bannerCurrentlyShowing = true;
						this.showTimeoutBanner$.emit(true);
					}
				} else if (timeoutTimer > 15 && this.bannerCurrentlyShowing) {
					this.bannerCurrentlyShowing = false;
					this.showTimeoutBanner$.emit(false);
				}

				if (timeoutTimer <= 0) {
					this.authenticationDelete({
						detailedMsg: 'Please enter your login information again to continue using Rapidship.',
						generalMsg: 'You have been logged out.'
					});
				}
			}
		});
	}



	/**
	 * @api {get} /v2/InetUserProfile/{username} Get Inet User Profile and Token by ID
	 * @apiName getInetUserProfile
	 * @apiGroup InetUsers
	 * @apiVersion 20.0.0
	 */
	completeLoginAfterSso(token) {
		return new Observable((observer) => {
			this._http.get<any>(`${environment.apiUrl}/v2/InetUserProfile/${token}`)
				.subscribe({
					next: (response) => {
						//this is going to be whatever user is logged into the SSO provider
						let credentials = {
							username: response.profile.username.toLocaleLowerCase(),
							rememberMe: false
						};
						//compare that to the user that tried to log into Rapidship
						let ssoInfo = JSON.parse(localStorage.getItem('RapidShip:ssoInfo'));
						localStorage.removeItem('RapidShip:ssoInfo'); //also delete this item because we don't need it anymore
						if (credentials.username !== ssoInfo.username) {
							//if it didn't match, set up authentication so we can delete the token that was created on the backend
							this.user = new User();
							this.user.username = credentials.username;
							this.user.token = response.token ? response.token : response;
							localStorage.setItem('Rapidship:user', JSON.stringify(this.user));
							this._http.delete<any>(`${environment.apiUrl}/v2/Authentication`).subscribe({
								next: () => {
									localStorage.removeItem('Rapidship:user'); //we don't need this anymore
									//show the login form with an explanatory error
									this._modalService.displayLoginModal(
										{message: `We are sorry for the inconvenience, but unfortunately your SSO provider does not allow for authentication to our system while more than one SSO user is logged into a session. Please make sure to log out of all sessions for ${ssoInfo.domain} except for your ${ssoInfo.username} account, then please try and log back into our portal with this account.`});
								},
								error: (error) => {
									localStorage.removeItem('Rapidship:user'); //we don't need this anymore
									observer.error(error);
								}
							});
						} else {
							// isSsoSession = true. It must be passed here rather than read later: the only
							// client-side trace of an SSO login is 'RapidShip:ssoInfo', and it has already been
							// removed above by the time the user object is assembled.
							this.loginInitiationSuccess(credentials, 'inet', observer, response, true);
						}
					},
					error: (err) => {
						observer.error(err);
					}
				});
		});
	}

}
